/* eslint-disable no-param-reassign */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Select from '../../juristec-ui/core/SelectNew';
import SelectTips from '../Popovers/SelectTips';
import KpiFilter from '../KpiFilter';
import Button from '../../juristec-ui/core/Button';
import KpiFilterApplied from '../KpiFilterApplied';
import registerNamespaceModifier from './locales';
import { dateOptions } from '../../options';
import {
  optionNamespace,
  GroupOptions,
  useKpiModifiers,
  useModifierConfig,
  TypePeriodOptions,
} from '../../hooks/modifiers';

import {
  ModifColumn, ModifRow, ModifsContainer, LabelSmall,
} from './styled/LabelCreate.styled';

const namespace = registerNamespaceModifier('src/components/LabelCreate/Modifier.jsx');

const modifierDefaultPropColumnOpts = [];
const modifierDefaultPropAddedModfs = [];

const Modifier = ({
  columnOpts = modifierDefaultPropColumnOpts,
  getUniqueValues,
  addedModfs = modifierDefaultPropAddedModfs,
  handleAddedModifs,
  atModal = false,
}) => {
  const { t } = useTranslation(namespace);
  const dataFrameTypes = useRef({});

  useEffect(() => {
    dataFrameTypes.current = columnOpts.reduce((aux, cOpt) => {
      aux[cOpt.value] = cOpt.type;
      return aux;
    }, {});
  }, [columnOpts]);

  const {
    modifierConfig,
    simpleOptions,
    getColumnOptions,
    getSimpleOptions,
    handleModifierConfigs,
    resetModifierConfigs,
    //
    configColumnIsDate,
    getOptionsByConfigGroup,
    checkModifierValues,
  } = useModifierConfig({
    dataFrameTypes: dataFrameTypes.current,
    modifierOrigins: { filter: t('filter') },
    getUniqueValues,
  });

  const handleModifierChange = (newModifiers, origin) => {
    handleAddedModifs(newModifiers[origin]);
  };

  const {
    modifiers,
    addModifier,
    removeModifier,
    editModifier,
    changeModifierOrder,
  } = useKpiModifiers({
    dataFrameTypes: dataFrameTypes.current,
    initialModifiers: { filter: addedModfs },
    onModifierChange: handleModifierChange,
  });

  const handleColumnSelect = (selected) => handleModifierConfigs({ column: selected });
  const handleGroupSelect = (selected) => handleModifierConfigs({ group: selected });
  const handleSelectorSelect = (selected) => handleModifierConfigs({ selector: selected });
  const handleFormatSelect = (selected) => handleModifierConfigs({ format: selected });
  const handleFilterChange = (values) => handleModifierConfigs({ values });

  const handleAddModifier = () => {
    addModifier(modifierConfig);
    resetModifierConfigs();
  };

  return (
    <>
      <ModifColumn>
        <ModifRow>
          <div>
            <Select
              label={t('filterBy')}
              onChange={handleColumnSelect}
              value={modifierConfig.column}
              options={getColumnOptions({ filter: columnOpts })}
              atModal
              searchable
              clearable
              sortBy="ascending"
            />
          </div>
          <div>
            <Select
              label={t('type')}
              onChange={handleGroupSelect}
              value={modifierConfig.group}
              options={GroupOptions.list}
              optionNamespace={optionNamespace}
              atModal
            />
          </div>
        </ModifRow>
        <ModifRow>
          {modifierConfig.column?.value?.length > 0
          && modifierConfig.group.value === GroupOptions.enum.SIMPLE
          && configColumnIsDate()
          && (
            <div>
              <Select
                label={t('format')}
                onChange={handleSelectorSelect}
                value={modifierConfig.selector}
                options={dateOptions}
                searchable
                atModal
              />
            </div>
          )}
          {modifierConfig.column?.value?.length > 0 && (
            <div style={{ position: 'relative' }}>
              <Select
                label={t('condition')}
                onChange={handleFormatSelect}
                value={modifierConfig.format}
                options={getOptionsByConfigGroup()}
                optionNamespace={optionNamespace}
                searchable
                atModal
              />
              {modifierConfig.format.value !== '' && (
                <SelectTips
                  description={t(modifierConfig.format.description, {
                    ns: optionNamespace,
                    ...(modifierConfig.format.params ?? {}),
                  })}
                  style={{ top: '5px', right: '5px' }}
                />
              )}
            </div>
          )}
        </ModifRow>
        {modifierConfig.column?.value?.length > 0
        && modifierConfig.format?.value.length > 0
        && (modifierConfig.group?.value === GroupOptions.enum.ADVANCED || simpleOptions.length > 0)
        && (
          <>
            {!Object.keys(TypePeriodOptions.map).includes(modifierConfig.format.value) && (
              <KpiFilter
                filterType={modifierConfig.format.value}
                handle={handleFilterChange}
                values={modifierConfig.values}
                options={getSimpleOptions(modifiers)}
                format={modifierConfig.column.type}
                atModal={atModal}
              />
            )}
            <Button
              onClick={handleAddModifier}
              color="secondary"
              disabled={!checkModifierValues()}
              style={{ margin: '10px 0 10px', width: '100%' }}
            >
              {t('addFilter')}
            </Button>
          </>
        )}
      </ModifColumn>
      {addedModfs.length > 0 && (
        <ModifsContainer>
          <LabelSmall>
            {t('filtersApplied')}
          </LabelSmall>
          {addedModfs.map((modif, i) => (
            <KpiFilterApplied
              key={modif.uid}
              origin="filter"
              modifier={modif}
              modifiers={modifiers}
              removeFunc={removeModifier}
              editFunc={editModifier}
              getOptions={getUniqueValues}
              dataFrameTypes={dataFrameTypes.current ?? {}}
              position={i}
              isLast={addedModfs.length - 1 === i}
              changePosition={changeModifierOrder}
              atModal
            />
          ))}
        </ModifsContainer>
      )}
    </>
  );
};

Modifier.propTypes = {
  columnOpts: PropTypes.arrayOf(PropTypes.shape({})),
  addedModfs: PropTypes.arrayOf(PropTypes.shape({})),
  handleAddedModifs: PropTypes.func.isRequired,
  atModal: PropTypes.bool,
  getUniqueValues: PropTypes.func.isRequired,
  selectedDatabase: PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
};

export default Modifier;
